import { Badge, Button, Card, CardHeader, EmptyState, Skeleton } from "@helmr/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isApiError, newIdempotencyKey } from "../../api/client";
import { dryRun, getReadiness, getReport, signOff, startCutover, type ImportJob } from "../../api/imports";
import { useServices } from "../../app/services";
import { ConfirmDrawer, Load } from "../settings/kit";
import s from "../settings/settings.module.css";
import o from "./onboarding.module.css";
import { Problems, Summary } from "./ReviewStep";
import { StepError, isConflict, useImportScope } from "./shared";
import { OPERATION_ORDER, operationLabel } from "./state";
import { t } from "./strings";

/** Cutover is considered stalled by the server after 30 minutes without progress (CUTOVER_STALL_MS); from then Resume is offered. */
export const STALL_MS = 30 * 60_000;

/** The dry run did not pass: say what failed, offer another go. */
export function RehearseStep({ job, onReload, onEdit }: { job: ImportJob; onReload: () => void; onEdit: (to: "upload" | "mapping") => void }) {
  const { client } = useServices();
  const sc = useImportScope(job.id);
  const again = useMutation({
    mutationFn: () => dryRun(client, job.id),
    onSuccess: (r) => { sc.putJob(r.job); sc.qc.setQueryData(sc.reportKey, { status: r.job.status, report: r.report }); },
    onSettled: () => void sc.qc.invalidateQueries({ queryKey: sc.jobKey }),
  });
  const failures = job.counts?.failures ?? [];
  return (
    <Card aria-label="The rehearsal did not pass">
      <CardHeader title="The rehearsal did not pass" subtitle="Nothing was written to your gym. Fix what is listed, then run it again." action={<Badge tone="crit">Failed</Badge>} />
      {job.error ? <div className={s.formErr} role="alert">{job.error}</div> : null}
      {failures.length > 0 ? (
        <div className={o.tableWrap}>
          <table aria-label="Operations that failed">
            <thead><tr><th>#</th><th>What</th><th>Why</th></tr></thead>
            <tbody>{failures.map((f) => <tr key={f.key}><td>{f.seq}</td><td>{operationLabel(f.type)}</td><td>{f.message}</td></tr>)}</tbody>
          </table>
        </div>
      ) : null}
      {job.counts?.failureCount && job.counts.failureCount > failures.length ? <div className={s.note}>Showing {failures.length} of {job.counts.failureCount} failures.</div> : null}
      <StepError error={again.error} onReload={onReload} />
      <div className={s.bar}>
        <span className={s.acts}><Button onClick={() => onEdit("upload")}>Change sheets</Button><Button onClick={() => onEdit("mapping")}>Change columns</Button></span>
        <Button variant="primary" busy={again.isPending} busyLabel="Rehearsing…" onClick={() => again.mutate()}>Run the rehearsal again</Button>
      </div>
    </Card>
  );
}

/** Rehearsal passed: the owner reads the report and signs, bound to the plan key, by typing the gym's name. */
export function SignOffStep({ job, onReload }: { job: ImportJob; onReload: () => void }) {
  const { client } = useServices();
  const sc = useImportScope(job.id);
  const report = useQuery({ queryKey: sc.reportKey, queryFn: () => getReport(client, job.id), retry: false });
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const gym = sc.ctx.me?.business.name ?? "";
  const sign = useMutation({ mutationFn: () => signOff(client, job.id, { planKey: job.planKey!, acceptedWarnings: accepted }), onSuccess: (r) => { setOpen(false); sc.putJob(r.job); } });
  return (
    <Load query={report} what="the report" skeleton={<Card aria-label="Loading report"><div className={s.skel} aria-busy="true"><Skeleton height={60} /><Skeleton height={34} /></div></Card>}>
      {({ report: r }) => (
        <div className={s.page}>
          <Summary job={job} r={r} />
          <Problems r={r} />
          <Card aria-label={t.steps.signoff.title}>
            <CardHeader title={t.steps.signoff.title} subtitle={t.steps.signoff.body} action={<Badge tone="ok">Rehearsal passed</Badge>} />
            {job.counts?.readback ? <div className={s.note}>The copy gained exactly what the plan says it should: {Object.entries(job.counts.readback).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ")}.</div> : null}
            {!sc.ctx.isOwner ? <div className={s.note}>Only the owner can sign off. Ask the owner to open this import.</div> : null}
            {r.warnings.total > 0 ? (
              <label className={s.check} style={{ padding: "0 16px" }}>
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} /> I have read the {r.warnings.total.toLocaleString()} warning(s) above.
              </label>
            ) : null}
            <div className={s.bar}>
              <span className={s.hint}>Signing binds to plan key <span className={o.hash}>{job.planKey?.slice(0, 16)}…</span></span>
              <Button variant="primary" disabled={!sc.ctx.isOwner || (r.warnings.total > 0 && !accepted)} onClick={() => setOpen(true)}>Sign off this plan</Button>
            </div>
          </Card>
          <ConfirmDrawer open={open} onClose={() => setOpen(false)} title="Sign off this import" description="You are approving exactly the counts shown. Nothing is written until you cut over." confirmLabel="Sign off" busyLabel="Signing…" busy={sign.isPending}
            typeToConfirm={gym} onConfirm={() => sign.mutate()} error={null}>
            <StepError error={sign.error} onReload={() => { setOpen(false); onReload(); }} />
          </ConfirmDrawer>
        </div>
      )}
    </Load>
  );
}

/** Approved → cutover → done. Progress comes from polling the job; leaving the page does not stop it. */
export function CutoverStep({ job, onReload }: { job: ImportJob; onReload: () => void }) {
  const { client, now } = useServices();
  const sc = useImportScope(job.id);
  // One Idempotency-Key per intent: a retry of the same click replays the stored 202; a deliberate Resume gets a fresh key.
  const key = useRef(newIdempotencyKey());
  // The server judges a stall by the last applied batch, which the job view does not carry (the consumer never bumps
  // updatedAt). When it refuses a Resume with 409 "already running", that refusal is proof of recent progress.
  const [seenRunningAt, setSeenRunningAt] = useState(0);
  const start = useMutation({
    mutationFn: () => startCutover(client, job.id, key.current),
    onSuccess: (r) => { key.current = newIdempotencyKey(); sc.putJob(r.job); },
    // A refusal (4xx) is an answer, so the next click is a new intent. A dropped connection or a 5xx may have started it: keep the key and the retry replays.
    onError: (e) => {
      if (isApiError(e) && e.status >= 400 && e.status < 500) key.current = newIdempotencyKey();
      if (job.status === "cutting_over" && isConflict(e)) { setSeenRunningAt(now()); void sc.qc.invalidateQueries({ queryKey: sc.jobKey }); }
    },
  });
  const plan = job.counts?.plan ?? {};
  const running = job.status === "cutting_over";
  const stillRunning = running && seenRunningAt > 0 && isConflict(start.error);
  const stalled = running && now() - Math.max(job.updatedAt, seenRunningAt) >= STALL_MS;
  const resuming = job.status === "failed" || stalled;
  return (
    <Card aria-label={t.steps.cutover.title}>
      <CardHeader title={t.steps.cutover.title} subtitle={running ? "Cutover is running. You can leave this page; it keeps going." : t.steps.cutover.body}
        action={job.status === "failed" ? <Badge tone="crit">Failed</Badge> : running ? <Badge tone="info">Running</Badge> : <Badge tone="ok">Signed off</Badge>} />
      {job.status === "failed" ? (
        <div className={s.formErr} role="alert">
          <b>Cutover stopped part-way.</b> {job.error ?? "A batch failed."}
          <div>Rows already written stay written. Resume picks up where it stopped and never writes a row twice.</div>
        </div>
      ) : null}
      {stalled ? <Warn2>No progress for over 30 minutes. Resume to continue.</Warn2> : null}
      <div className={o.stats} role="list" aria-label="Operations in the signed plan">
        {OPERATION_ORDER.filter((k) => (plan[k] ?? 0) > 0).map((k) => <div key={k} role="listitem" className={o.stat}><b className={o.count}>{(plan[k] ?? 0).toLocaleString()}</b><span>{operationLabel(k)}</span></div>)}
      </div>
      {running && !stalled ? <div className={o.bar} role="progressbar" aria-label="Cutover running" aria-valuetext="Running"><i /></div> : null}
      {!running || stalled ? <div className={s.note}>Messaging is paused during cutover and stays paused afterwards. You turn it on yourself once you have checked members, plans and dues.</div> : null}
      {!sc.ctx.isOwner ? <div className={s.note}>Only the owner can run the cutover.</div> : null}
      {stillRunning ? <Warn2>The cutover is still running: Helmr saw a batch written recently. Leave it to finish.</Warn2> : <StepError error={start.error} onReload={onReload} />}
      <div className={s.bar}>
        <span className={s.hint}>{job.status === "approved" ? "Pick a quiet time. Cancel is not possible once cutover begins." : "Progress refreshes every few seconds."}</span>
        {(!running || stalled) ? <Button variant="primary" busy={start.isPending} busyLabel="Starting…" disabled={!sc.ctx.isOwner} onClick={() => start.mutate()}>{resuming ? "Resume cutover" : "Start cutover"}</Button> : null}
      </div>
    </Card>
  );
}

const Warn2 = ({ children }: { children: React.ReactNode }) => <div className={s.note} role="status">{children}</div>;

export function DoneStep({ job }: { job: ImportJob }) {
  const { client } = useServices();
  const sc = useImportScope(job.id);
  const ready = useQuery({ queryKey: sc.readyKey, queryFn: () => getReadiness(client, job.id), retry: false });
  const plan = job.counts?.plan ?? {};
  return (
    <div className={s.page}>
      <Card aria-label="Import done">
        <CardHeader title="Import done" subtitle="Your members, plans and dues are in Helmr." action={<Badge tone="ok">Done</Badge>} />
        <div className={o.stats} role="list">
          {OPERATION_ORDER.filter((k) => (plan[k] ?? 0) > 0).map((k) => <div key={k} role="listitem" className={o.stat}><b className={o.count}>{(plan[k] ?? 0).toLocaleString()}</b><span>{operationLabel(k)}</span></div>)}
        </div>
        <div className={s.note}>Messaging is still paused. Spot-check a few members, plans and dues, then turn it on in <Link to="/settings/messaging">Messaging</Link>. Uploaded sheets are deleted 30 days after cutover.</div>
      </Card>
      <Card aria-label="Readiness">
        <CardHeader title="Ready for go-live?" subtitle="A checklist for the things around the import. It informs; it does not block." />
        <Load query={ready} what="the readiness list" skeleton={<div className={s.skel}><Skeleton height={34} /><Skeleton height={34} /></div>}>
          {(d) => d.checks.length === 0 ? <EmptyState compact icon="receipt" title="Nothing to check" /> : (
            <div role="list" aria-label="Readiness checks">
              {d.checks.map((c) => (
                <div key={c.key} role="listitem" className={o.ready}>
                  <span aria-hidden="true">{c.state === "ok" ? "✓" : c.state === "todo" ? "○" : "•"}</span>
                  <span><b>{c.label}</b> <Badge tone={c.state === "ok" ? "ok" : c.state === "todo" ? "warn" : "neutral"}>{c.state === "ok" ? "Done" : c.state === "todo" ? "To do" : "Note"}</Badge></span>
                  <small>{c.detail}</small>
                </div>
              ))}
            </div>
          )}
        </Load>
      </Card>
    </div>
  );
}
